import React, { useState, useRef, useEffect } from 'react';
import { Popover, Checkbox, Input } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { formatDate, formatDateTime } from '../../../Utils/dateFormat';

const ColumnFilterHeader = ({ column, label, formatValue, emptyText, splitValues }) => {
  const facetedMap = column.getFacetedUniqueValues?.() ?? new Map();
  const rawOptions = [...facetedMap.keys()].filter(v => v != null && v !== '');
  const options = [
    ...new Set(
      splitValues
        ? rawOptions.flatMap(v => String(v).split(', ').filter(Boolean))
        : rawOptions.map(v => (formatValue ? formatValue(v) : v))
    )
  ]
    .filter(Boolean)
    .sort();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const currentFilter = column.getFilterValue() || [];

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleCheckboxChange = (value, checked) => {
    let newFilter;
    if (checked) {
      newFilter = [...currentFilter, value];
    } else {
      newFilter = currentFilter.filter(f => f !== value);
    }
    column.setFilterValue(newFilter.length > 0 ? newFilter : undefined);
  };

  const isActive = currentFilter.length > 0;

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 4 }}
      ref={dropdownRef}
    >
      <span>{label}</span>
      <FilterOutlined
        onClick={e => {
          e.stopPropagation();
          setOpen(prev => {
            if (!prev) setSearch('');
            return !prev;
          });
        }}
        style={{
          cursor: 'pointer',
          color: isActive ? '#1890ff' : 'rgba(0,0,0,0.25)',
          fontSize: 12
        }}
      />
      {open && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 100,
            background: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: 8,
            minWidth: 200
          }}
        >
          <Input
            size='small'
            placeholder='Search...'
            value={search}
            onChange={e => setSearch(e.target.value)}
            onClick={e => e.stopPropagation()}
            allowClear
            style={{ marginBottom: 6 }}
          />
          {isActive && (
            <div
              style={{
                padding: '2px 8px 4px',
                textAlign: 'right'
              }}
            >
              <span
                onClick={() => column.setFilterValue(undefined)}
                style={{
                  color: '#1890ff',
                  cursor: 'pointer',
                  fontSize: 12
                }}
              >
                Clear All
              </span>
            </div>
          )}
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {options
              .filter(name =>
                String(name).toLowerCase().includes(search.toLowerCase())
              )
              .map(name => (
                <div key={name} style={{ padding: '4px 8px' }}>
                  <Checkbox
                    checked={currentFilter.includes(name)}
                    onChange={e => handleCheckboxChange(name, e.target.checked)}
                  >
                    <span style={{ fontSize: 13 }}>{name}</span>
                  </Checkbox>
                </div>
              ))}
            {options.filter(name =>
              String(name).toLowerCase().includes(search.toLowerCase())
            ).length === 0 && (
              <div style={{ padding: '4px 8px', color: '#999', fontSize: 13 }}>
                {search ? 'No matches' : emptyText || 'No options'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const checkboxFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const cellValue = row.getValue(columnId);
  return filterValue.includes(cellValue);
};

const cptCodesFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const cellValue = row.getValue(columnId);
  if (!cellValue || cellValue === 'N/A') return false;
  const codes = cellValue.split(', ');
  return filterValue.some(f => codes.includes(f));
};

const STATUS_LABELS = {
  INPROGRESS_FOR_DOCTOR: 'In-Progress for Doctor',
  READY_FOR_REVIEWER: 'Ready for Reviewer',
  INPROGRESS_FOR_REVIEWER: 'In-Progress for Reviewer',
  EMR_SUBMITTED: 'EMR Submitted',
  EMR_SUBMITTED_BILLED: 'EMR Submitted Billed',
  EMR_SUBMITTED_NOT_BILLED: 'EMR Submitted Not Billed',
  DELETED: 'Deleted',
  EMR_SUBMISSION_FAILED: 'EMR Submission Failed',
  STRUCK: 'Struck'
};

const formatStatus = value => STATUS_LABELS[value] || value || null;

const statusFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const value = row.getValue(columnId);
  return filterValue.includes(formatStatus(value));
};

const formatDateOnly = value => formatDate(value, null);

const RISK_ORDER = ['HIGH', 'MODERATE_HIGH', 'LOW_MODERATE'];

const riskSortingFn = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);
  const ai = RISK_ORDER.indexOf(a);
  const bi = RISK_ORDER.indexOf(b);
  const aRank = ai === -1 ? RISK_ORDER.length : ai;
  const bRank = bi === -1 ? RISK_ORDER.length : bi;
  return aRank - bRank;
};

const dateOnlyFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const value = row.getValue(columnId);
  const formatted = formatDateOnly(value);
  if (!formatted) return filterValue.includes('--');
  return filterValue.includes(formatted);
};

/**
 * Create column definitions for TanStack Table in NotesBoardPage
 * Uses raw API data (not pre-mapped)
 */
export const createNotesBoardColumns = () => {
  return [
    {
      id: 'jobId',
      accessorKey: 'jobNumber',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Job ID'
          emptyText='No job IDs'
        />
      ),
      size: 100,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => getValue() || 'N/A'
    },
    {
      id: 'author',
      accessorKey: 'author',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Author'
          emptyText='No authors'
        />
      ),
      size: 150,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 20 ? val.slice(0, 20) + '...' : val}
          </span>
        );
      }
    },
    {
      id: 'primaryCarePhysician',
      accessorKey: 'primaryCarePhysician',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Primary Care Physician'
          emptyText='No data'
        />
      ),
      size: 210,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 20 ? val.slice(0, 20) + '...' : val}
          </span>
        );
      }
    },
    {
      id: 'patientName',
      accessorKey: 'patientName',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Patient Name'
          emptyText='No patients'
        />
      ),
      size: 150,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 20 ? val.slice(0, 20) + '...' : val}
          </span>
        );
      }
    },
    {
      id: 'submissionTime',
      accessorKey: 'submissionTime',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Submitted Time'
          formatValue={formatDateOnly}
          emptyText='No dates'
        />
      ),
      size: 180,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: dateOnlyFilterFn,
      cell: ({ getValue }) => formatDateTime(getValue(), 'N/A')
    },
    {
      id: 'effectiveDate',
      accessorKey: 'effectiveDate',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Encounter Date'
          formatValue={formatDateOnly}
          emptyText='No dates'
        />
      ),
      size: 180,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: dateOnlyFilterFn,
      cell: ({ getValue }) => formatDateTime(getValue(), 'N/A')
    },
    {
      id: 'facility',
      accessorFn: row => row.facility?.facilityName,
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Facility'
          emptyText='No facilities'
        />
      ),
      size: 170,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <Popover placement='bottom' content={<h5>{val}</h5>}>
            <span title={val}>{val}</span>
          </Popover>
        );
      }
    },
    {
      id: 'currentAssignee',
      accessorKey: 'currentAssignee',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Current Assignee'
          emptyText='No assignees'
        />
      ),
      size: 180,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => getValue() || 'N/A'
    },
    {
      id: 'biller',
      accessorFn: row => row.biller?.fullName,
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Biller'
          emptyText='No billers'
        />
      ),
      size: 150,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => getValue() || 'N/A'
    },
    {
      id: 'workType',
      accessorKey: 'workType',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Work Type'
          emptyText='No work types'
        />
      ),
      size: 150,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => getValue() || 'N/A'
    },
    {
      id: 'dictationTemplate',
      accessorFn: row => row.dictationTemplate?.templateName,
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Template'
          emptyText='No templates'
        />
      ),
      size: 150,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return val;
      }
    },
    {
      id: 'submissionToReviewerLevel1Time',
      accessorKey: 'submissionToReviewerLevel1Time',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Submission Time to Reviewer'
          formatValue={formatDateOnly}
          emptyText='No dates'
        />
      ),
      size: 280,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: dateOnlyFilterFn,
      cell: ({ getValue }) => formatDateTime(getValue(), 'N/A')
    },
    {
      id: 'rejectedByReviewerTime',
      accessorKey: 'rejectedByReviewerTime',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Reviewer Rejected Time'
          formatValue={formatDateOnly}
          emptyText='No dates'
        />
      ),
      size: 240,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: dateOnlyFilterFn,
      cell: ({ getValue }) => formatDateTime(getValue(), 'N/A')
    },
    {
      id: 'billedTime',
      accessorKey: 'billedTime',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Billed Time'
          formatValue={formatDateOnly}
          emptyText='No dates'
        />
      ),
      size: 180,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: dateOnlyFilterFn,
      cell: ({ getValue }) => formatDateTime(getValue(), 'N/A')
    },
    {
      id: 'emrSubmissionTime',
      accessorKey: 'emrSubmissionTime',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='EMR Submission Time'
          formatValue={formatDateOnly}
          emptyText='No dates'
        />
      ),
      size: 220,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: dateOnlyFilterFn,
      cell: ({ getValue }) => formatDateTime(getValue(), 'N/A')
    },
    {
      id: 'scribe',
      accessorFn: row => row.scribe?.fullName,
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Scribe'
          emptyText='No scribes'
        />
      ),
      size: 150,
      minSize: 80,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val) return 'N/A';
        return (
          <span title={val}>
            {val.length > 20 ? val.slice(0, 20) + '...' : val}
          </span>
        );
      }
    },
    {
      id: 'riskAssessment',
      accessorFn: row => row.patientAssignmentData?.risk,
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Risk Assessment'
          emptyText='No risk data'
        />
      ),
      size: 150,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      sortingFn: riskSortingFn,
      cell: ({ getValue }) => getValue() || 'N/A'
    },
    {
      id: 'patientType',
      accessorFn: row => row.patientAssignmentData?.patientTermType,
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Patient Type'
          emptyText='No patient type data'
        />
      ),
      size: 150,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => getValue() || 'N/A'
    },
    {
      id: 'admissionDate',
      accessorFn: row => row.patientDemographics?.admissionDate,
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Admission Date'
          formatValue={formatDateOnly}
          emptyText='No admission dates'
        />
      ),
      size: 160,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: dateOnlyFilterFn,
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return 'N/A';
        return formatDateOnly(value);
      }
    },
    {
      id: 'mrn',
      accessorFn: row => row.patientDemographics?.medicalRecordNumber,
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='MRN'
          emptyText='No MRN data'
        />
      ),
      size: 150,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: checkboxFilterFn,
      cell: ({ getValue }) => getValue() || 'N/A'
    },
    {
      id: 'dischargeDate',
      accessorFn: row => row.patientDemographics?.dischargeDate,
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Discharge Date'
          formatValue={formatDateOnly}
          emptyText='No discharge dates'
        />
      ),
      size: 160,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: dateOnlyFilterFn,
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return 'N/A';
        return formatDateOnly(value);
      }
    },
    {
      id: 'cptCodes',
      accessorFn: row => {
        const codes = row.cptCodesInContent;
        if (!codes || codes.length === 0) return 'N/A';
        return codes.map(c => c.cptCode).filter(Boolean).join(', ') || 'N/A';
      },
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='CPT Codes'
          emptyText='No CPT codes'
          splitValues
        />
      ),
      size: 180,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: cptCodesFilterFn,
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => (
        <ColumnFilterHeader
          column={column}
          label='Status'
          formatValue={formatStatus}
          emptyText='No statuses'
        />
      ),
      size: 150,
      minSize: 40,
      maxSize: 500,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: statusFilterFn,
      cell: ({ getValue }) => formatStatus(getValue()) || 'N/A'
    }
  ];
};
